import { useEffect, useRef, useState } from "react";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyFunction = (...args: any[]) => any;

interface DebouncedFunction<T extends AnyFunction> {
  (...args: Parameters<T>): void;
  cancel: () => void;
  flush: () => void;
}

/**
 * Creates a stable debounced function that always calls the latest callback.
 * The returned function has a stable identity (safe in deps arrays and as props)
 * and exposes `.cancel()` and `.flush()`.
 *
 * Implementation notes:
 * - `callbackRef` / `delayRef` are updated in `useEffect` (not during render)
 *   so the React Compiler cannot memoize away the assignment.
 * - The debounced function is created once via `useState` lazy initializer,
 *   avoiding render-time ref reads that the compiler would flag.
 */
export function useDebouncedCallback<T extends AnyFunction>(
  callback: T,
  delay: number,
): DebouncedFunction<T> {
  const callbackRef = useRef(callback);
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  const delayRef = useRef(delay);
  useEffect(() => {
    delayRef.current = delay;
  }, [delay]);

  const [debouncedFn] = useState<DebouncedFunction<T>>(() => {
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    let pendingArgs: Parameters<T> | null = null;

    const debounced = (...args: Parameters<T>) => {
      pendingArgs = args;
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        timeoutId = null;
        callbackRef.current(...pendingArgs!);
        pendingArgs = null;
      }, delayRef.current);
    };

    debounced.cancel = () => {
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = null;
      pendingArgs = null;
    };

    debounced.flush = () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
        callbackRef.current(...pendingArgs!);
        pendingArgs = null;
      }
    };

    return debounced;
  });

  useEffect(() => {
    return () => debouncedFn.cancel();
  }, [debouncedFn]);

  return debouncedFn;
}
